<template>
  <div class="alignment" v-if="currentAlignment !== null">
    <Portal to="aw-additional-controls">
      <div class="alignment-meta-container">
        <div class="actions-row actions-row-pager">
          <div class="alignment-works">
            <div>
              <span v-if="!maxSelected" class="add-button" @click="onAdd('left')">
                <icon name="plus" />
              </span>
              {{ leftWork }}
            </div>
            <div>
              <span v-if="!maxSelected" class="add-button" @click="onAdd('right')">
                <icon name="plus" />
              </span>
              {{ rightWork }}
            </div>
          </div>
          <div class="pager-controls">
            <LockToggle v-model="locked" />
            <Paginator :next="nextChunk" :previous="prevChunk" @paginate="onPaginate" />
            <div class="spacer"></div>
            <div class="close" @click.prevent="$emit('close')"><icon name="times-circle" /></div>
          </div>
        </div>
        <div class="actions-row actions-row-mode-toggle">
          <div class="button-group">
            <div class="button" :class="{active: !overlay}" @click.prevent="toggleMode">{{ $t('alignment.sideBySideLabel') }}</div>
            <div class="button" :class="{active: overlay}" @click.prevent="toggleMode">{{ $t('alignment.overlayLabel') }}</div>
          </div>
        </div>
        <div class="title-row" v-if="gqlData && !overlay">
          <span class="title">{{ leftWork }}</span>
          <div class="title" @click.prevent="onRightTitleClick">{{ rightWork }}</div>
        </div>
        <div class="title-row" v-else-if="gqlData && overlay">
          <div class="overlay-legend">
            <span class="title">{{ localizedWorkTitle(gqlData.alignmentChunk.leftWork) }}</span>
            <div class="insertions">
              <span class="ins-legend">{{ $t('textAlignmentsWidget.overlayLegend.insertions') }}</span>
              <span class="del-legend">{{ $t('textAlignmentsWidget.overlayLegend.deletions') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Portal>
    <LoaderBall v-if="this.loadingGqlData" class="overlay-loader" :style="loaderStyles"/>
    <template v-else-if="overlayData">
      <div class="overlay" v-if="overlay" v-html="overlayData.overlay_chunk" ref="alignment-content"></div>
      <div class="side-by-side" v-else ref="alignment-content">
        <div class="side-by-side--left" v-html="overlayData.left_text_chunk"></div>
        <div class="side-by-side--right" v-html="overlayData.right_text_chunk"></div>
      </div>
    </template>
  </div>
  <div class="alignment" v-else-if="alignments.length > 0">
    <div class="alignment-choices">
      <p>{{ $t('textAlignmentsWidget.multipleAlignments') }}</p>
      <div
        class="alignment-choice"
        v-for="(align, index) in alignments" :key="align.id"
        @click.prevent="onSelect(align)"
      >
        {{ getAlignmentLabel(align, index) }}
      </div>
    </div>
  </div>
  <div class="alignment alignment-help" v-else>
    <p>{{ $t('textAlignmentsWidget.helpText') }}</p>
  </div>
</template>

<script>
  import { v4 as uuidv4 } from 'uuid';
  import { Portal } from 'portal-vue';

  import LockToggle from './LockToggle.vue';
  import LoaderBall from './LoaderBall.vue';
  import Paginator from './Paginator.vue';
  import mixins from '../mixins';
  import utils from '../utils';
  import {
    SELECT_ALIGNMENT, PAGINATE_ALIGNMENT, TOGGLE_TEXT_REUSE_READER_LOCK, ADD_NEW_READER, REPLACE_READERS,
  } from '../constants';

  export default {
    mixins: [mixins.LocalizedWorkTitleMixin, mixins.RouteReaderSelectionsMixin],
    props: ['alignments'],
    components: {
      LockToggle, Paginator, Portal, LoaderBall,
    },
    data() {
      return {
        overlay: false,
        currentIndex: 0,
        syncedUrns: [],
        lastContentHeight: '70vh',
      };
    },
    watch: {
      locked: {
        handler(value) {
          if (value) {
            // lock has been toggled on; we should synchronize the readers
            this.syncReaders();
          } else {
            // the lock is being toggled off; we should reset syncedUrns
            this.syncedUrns = [];
          }
        },
      },
      alignmentChunkIdx: {
        handler() {
          if (this.locked) {
            // gqlData has finished firing and we're locked;
            // time to sync the readers
            this.syncReaders();
          }
        },
      },
      '$route.query.urn': {
        handler(value) {
          if (this.locked && this.urnsOutOSync(value)) {
            // toggle the lock off if the selections have been modified
            // outside of syncReaders
            this.locked = false;
          }
        },
      },
      overlay: {
        handler() {
          // @@@ would be nice if we could use refs here rather than relying on
          // closest
          const controlsBlock = this.$el.closest('.widget').querySelector('.sticky-block');
          // detect if we've actually scrolled past the sticky block
          const delta = controlsBlock.offsetTop;
          if (delta) {
            // if we're past the sticky block, scroll back to its top
            const $sidebar = this.$el.closest('.sidebar');
            $sidebar.scrollTop -= delta;
          }
        },
      },
    },
    methods: {
      onPaginate(chunk) {
        if (!chunk) {
          return;
        }
        this.lastContentHeight = `${this.$refs['alignment-content'].clientHeight}px;`;
        const selectionAction = this.locked ? PAGINATE_ALIGNMENT : SELECT_ALIGNMENT;
        this.$store.dispatch(selectionAction, { alignments: [chunk] });
      },
      syncReaders() {
        const newReaders = [];
        newReaders.push({
          readerId: uuidv4(),
          selection: utils.parseId(this.gqlData.alignmentChunk.leftSelectionUrn),
        });
        newReaders.push({
          readerId: uuidv4(),
          selection: utils.parseId(this.gqlData.alignmentChunk.rightSelectionUrn),
        });
        this.$store.dispatch(REPLACE_READERS, newReaders).then(() => {
          this.syncUrns();
          this.syncedUrns = this.$store.state.readers.map(reader => reader.selection.id);
        });
      },
      urnsOutOSync(value) {
        const syncedUrnsString = this.syncedUrns.toString();
        return syncedUrnsString !== value.toString();
      },
      onAdd(direction) {
        const id = direction === 'left' ? this.gqlData.alignmentChunk.leftSelectionUrn : this.gqlData.alignmentChunk.rightSelectionUrn;
        const readerId = uuidv4();
        const selection = utils.parseId(id);
        this.$store.dispatch(ADD_NEW_READER, { readerId, selection }).then(() => {
          this.syncUrns();
        });
      },
      getLineRange(boundary) {
        const startLineIdx = boundary[0][0];
        const endLineIdx = boundary[1][0];
        return `${startLineIdx} - ${endLineIdx}`;
      },
      getAlignmentLabel(alignment) {
        return `${this.localizedWorkTitle(alignment.leftWork)} [${this.getLineRange(alignment.leftBoundary)}] :: ${this.localizedWorkTitle(alignment.rightWork)} [${this.getLineRange(alignment.rightBoundary)}]`;
      },
      onRightTitleClick() {
        this.$emit('addId', this.rightAlignmentIdParts);
      },
      onSelect(alignment) {
        this.$store.dispatch(SELECT_ALIGNMENT, { alignments: [alignment] });
      },
      toggleMode() {
        this.overlay = !this.overlay;
      },
    },
    computed: {
      loaderStyles() {
        // prevent the loader from "flashing"
        return `height: ${this.lastContentHeight};`;
      },
      locked: {
        get() {
          return this.$store.state.textReuseReaderLock;
        },
        set() {
          this.$store.dispatch(TOGGLE_TEXT_REUSE_READER_LOCK);
        },
      },
      leftWork() {
        return this.gqlData && this.localizedWorkTitle(this.gqlData.alignmentChunk.leftWork);
      },
      rightWork() {
        return this.gqlData && this.localizedWorkTitle(this.gqlData.alignmentChunk.rightWork);
      },
      currentAlignment() {
        return this.alignments.length === 1 ? this.alignments[0] : null;
      },
      alignmentChunkIdx() {
        return this.gqlData ? this.gqlData.alignmentChunk.idx : null;
      },
      rightAlignmentIdParts() {
        if (this.gqlData) {
          const {
            rightWork, rightBoundary, rightStart,
          } = this.gqlData.alignmentChunk;
          return {
            workUrn: rightWork.urn,
            sectionIdx: rightStart.section.idx,
            lineIdx: rightBoundary[0][0],
            tokenIdx: rightBoundary[0][1],
          };
        }
        return null;
      },
      overlayData() {
        if (this.gqlData) {
          return this.gqlData.alignmentChunk.overlayData;
        }
        return null;
      },
      textReuseMatchThreshold() {
        return this.$store.state.textReuseMatchThreshold;
      },
      gqlQuery() {
        if (this.currentAlignment === null) {
          return null;
        }
        return `{
          alignmentChunk: alignmentChunk(id:"${this.currentAlignment.id}") {
            overlayData
            leftSelectionUrn
            rightSelectionUrn
            leftWork {
              name
              urn
            }
            rightWork {
              name
              urn
            }
            rightBoundary
            rightStart {
              section {
                idx
              }
            }
            id
            idx
            leftBoundary
            rightBoundary
            leftWork {
              name
              urn
              id
            }
            rightWork {
              name
              urn
            }
          }
          nextChunk: alignmentChunks(
            leftWork: "${this.currentAlignment.leftWork.id}",
            idx_Gt: ${this.currentAlignment.idx},
            threshold_Gte: ${this.textReuseMatchThreshold}
            first: 1
            ) {
            edges {
              node {
                id
                idx
                leftBoundary
                rightBoundary
                leftWork {
                  name
                  urn
                  id
                }
                rightWork {
                  name
                  urn
                }
              }
            }
          }
          prevChunk: alignmentChunks(
            leftWork: "${this.currentAlignment.leftWork.id}",
            idx_Lt: ${this.currentAlignment.idx},
            threshold_Gte: ${this.textReuseMatchThreshold}
            first: 1
            ) {
            edges {
              node {
                id
                idx
                leftBoundary
                rightBoundary
                leftWork {
                  name
                  urn
                  id
                }
                rightWork {
                  name
                  urn
                }
              }
            }
          }
        }`;
      },
      prevChunk() {
        return this.gqlData && this.gqlData.prevChunk.edges.length
          ? this.gqlData.prevChunk.edges[0].node
          : null;
      },
      nextChunk() {
        return this.gqlData && this.gqlData.nextChunk.edges.length
          ? this.gqlData.nextChunk.edges[0].node
          : null;
      },
      leftStartLine() {
        return this.currentAlignment.leftBoundary[0][0];
      },
      leftStartToken() {
        return this.currentAlignment.leftBoundary[0][1];
      },
      leftEndLine() {
        return this.currentAlignment.leftBoundary[1][0];
      },
      leftEndToken() {
        return this.currentAlignment.leftBoundary[1][1];
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../variables.scss";

  #app[data-locale="en"] {
    .title-row,
    .alignment-choices,
    .alignments-back,
    .alignment-help p {
        font-family: $widget-font-family;
    }
    .actions-row {
      direction: ltr!important;
    }
  }

  #app[data-locale="ar"] {
    .actions-row-mode-toggle {
      font-family: $ar-font-family;
      font-size: $ar-default-font-size;
    }
    .title-row {
        font-family: $ar-font-family;
        font-size: $ar-text-md;
        line-height: 0.667em;
        margin-bottom: 12px;
        padding-top: 20px;
    }
    .alignment-choices {
      font-family: $ar-font-family;
    }
    .actions-row, .overlay-legend {
      direction: rtl !important;
    }
    .alignment-works {
      font-family: $ar-font-family;
      font-size: $ar-default-font-size;
    }
  }

  .side-by-side,
  .title-row {
    display: flex;
    justify-content: space-around;
    // mirror main reader
    flex-direction: row;
  }

  .alignment-help p {
    padding: 0 10px;
  }
  figure {
    width: 75%;
    margin: 0 auto;
    border: 1px solid $gray-100;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
    margin-bottom: 20px;
    border-radius: 5px;
    img {
      width: 100%;
    }
  }
  .alignment-choices {
    margin-left: 10px;
  }

  .alignment-choice {
    cursor: pointer;
    margin-bottom: 5px;
    padding: 5px;
    margin-left: -5px;
    &:hover {
      background: $gray-100;
      color: $brand;
    }
  }
  .overlay,
  .side-by-side {
    font-family: $ar-font-family;
    font-size: $ar-text-md;
    &::v-deep ins {
      text-decoration: none;
    }
    &::v-deep del {
      text-decoration: none;
    }
    &::v-deep span {
       background: lighten($insert, 30);
    }
    .side-by-side--left, .side-by-side--right {
      max-width: 50%;
      padding: 0 4px;
    }
    .side-by-side--left {
    }
    .side-by-side--right {
      background: #e9ecef;
    }
  }

  .overlay-legend {
    display: flex;
    flex-direction: row;
    .title {
      flex: 2;
    }
    .insertions {
      flex: 1;
      display: flex;
      flex-direction: row;
      .ins-legend {
        background: lighten($insert, 30);
        margin-inline-end: 0.5em;
        padding: 2px;
      }
      .del-legend {
        background: lighten($remove, 30);
        padding: 2px;
      }
    }
  }
  .overlay {
    &::v-deep span {
      background: unset;
    }
    &::v-deep ins {
      background: lighten($insert, 30);
      text-decoration: none;
    }
    &::v-deep del {
      background: lighten($remove, 30);
      text-decoration: none;
    }
  }
  .alignment-meta-container {
    font-size: 13pt;
    background: $gray-100;
    .actions-row {
      display: flex;
      justify-content: space-between;
      .close,
      .back {
        color: $gray-400;
        cursor: pointer;
        &:hover {
          color: $danger;
        }
      }
      .back:hover {
        color: $gray-800;
      }
      .button {
        font-size: 11pt;
        padding: 2px 5px;
        display: inline-block;
        background: $gray-200;
        border: 1px solid $gray-400;
        cursor: pointer;
        &.active,
        &:hover {
          background: $gray-400;
        }
      }
    }
    .actions-row-mode-toggle {
      padding-top: 10px;
      justify-content: space-evenly;
    }
    .title-row {
      padding-top: 10px;
      > * {
        flex: 1;
        text-align: center;
      }
    }
  }
  .alignment-meta-container {
    border: 1px solid $gray-200;
    padding: 5px;
    margin-bottom: 5px;
  }

  .alignment-works {
    width: calc(100% / 4 * 3);
  }
  .pager-controls {
    display: flex;
    justify-content: flex-end;
    width: calc(100% / 4 * 1);
  }
  .actions-row-pager {
    align-items: flex-start;
    justify-content: space-between;
  }
</style>
